import React, { useState } from 'react';
import * as Video from 'twilio-video';
import {
	Button,
	Container,
	Link,
	Typography,
	Paper,
	Grid,
	TextField,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
	container: {
		marginTop: '2.5em',
	},
	paper: {
		padding: '1em',
	},
	actions: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: '0.75em',
		margin: '1em 0',
	},
	heading: {
		marginBottom: '0.4em',
	},
});

// Safari's app handoff is best-effort; keep a copy option for browsers that block it.
// Replace only the outer scheme so query parameters and fragments stay unchanged.
export function safariHandoffUrl(url: string) {
	return url.startsWith('https://')
		? url.replace(/^https:/, 'x-safari-https:')
		: null;
}

export default function UnsupportedBrowserWarning({
	children,
}: {
	children: React.ReactElement;
}) {
	const classes = useStyles();
	const [copyStatus, setCopyStatus] = useState('');
	const [showLink, setShowLink] = useState(false);
	const userAgent = navigator.userAgent;
	const isIOS =
		/iPhone|iPad|iPod/.test(userAgent) ||
		(/Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1);
	const isFirefoxIOS = isIOS && /FxiOS/.test(userAgent);
	const callUrl = window.location.href;
	const safariUrl = isFirefoxIOS ? safariHandoffUrl(callUrl) : null;
	const copyLink = async () => {
		try {
			await navigator.clipboard.writeText(callUrl);
			setCopyStatus(
				'Link copied. Open Safari and paste it into the address bar.',
			);
			setShowLink(false);
		} catch {
			setCopyStatus(
				'Touch and hold the link below to copy it, then paste it into Safari.',
			);
			setShowLink(true);
		}
	};

	if (!Video.isSupported) {
		return (
			<Container>
				<Grid
					container
					justifyContent="center"
					className={classes.container}
				>
					<Grid item xs={12} sm={6}>
						<Paper className={classes.paper}>
							<Typography
								variant="h4"
								className={classes.heading}
							>
								{isFirefoxIOS
									? 'Open this video call in Safari'
									: 'Browser or context not supported'}
							</Typography>
							{isFirefoxIOS ? (
								<>
									<Typography>
										This video call isn’t supported in
										Firefox on iPhone or iPad. Open this
										same call in Safari to join.
									</Typography>
									<div className={classes.actions}>
										{safariUrl && (
											<Button
												variant="contained"
												color="primary"
												href={safariUrl}
											>
												Open in Safari
											</Button>
										)}
										<Button
											variant="outlined"
											onClick={copyLink}
										>
											Copy link
										</Button>
									</div>
									<Typography variant="body2">
										If Safari doesn’t open, copy the link
										and paste it into Safari’s address bar.
									</Typography>
									<Typography
										role="status"
										aria-live="polite"
									>
										{copyStatus}
									</Typography>
									{showLink && (
										<TextField
											id="unsupported-browser-call-link"
											label="Video call link"
											value={callUrl}
											fullWidth
											margin="normal"
											InputProps={{ readOnly: true }}
											onFocus={(event) =>
												event.target.select()
											}
										/>
									)}
								</>
							) : (
								<Typography>
									Please open this application in one of the{' '}
									<Link
										href="https://www.twilio.com/docs/video/javascript#supported-browsers"
										target="_blank"
										rel="noopener"
									>
										supported browsers
									</Link>
									.
									<br />
									If you are using a supported browser, please
									ensure that this app is served over a{' '}
									<Link
										href="https://developer.mozilla.org/en-US/docs/Web/Security/Secure_Contexts"
										target="_blank"
										rel="noopener"
									>
										secure context
									</Link>{' '}
									(e.g. https or localhost).
								</Typography>
							)}
						</Paper>
					</Grid>
				</Grid>
			</Container>
		);
	}

	return children;
}
